import { CONTRACTS, SPECIAL_CONTRACTS } from '../data/contracts';
import { LOCAL_CONTRACTS } from '../data/localExport';
import { EVENTS } from '../data/events';
import { VEHICLES } from '../data/vehicles';
import { RECURRENT_CLIENTS, isClientOnCooldown, getClientPriceMultiplier } from '../data/clientLoyalty';
import { pickContractOriginCity, FAR_CONTRACT_BOOST_MIN, FAR_CONTRACT_BOOST_SPAN } from '../data/depotGeo';
import { computeRegularFloorDaily } from '../data/economyBalance';
import { vehicleSatisfiesContract, driverSatisfiesContract, driverCanDriveVehicle } from '../utils/contractCompatibility';
import { getMaxContractTierForPhase } from './helpers/phaseProgression';

export const generateId = () => Math.random().toString(36).substring(2, 10);

/**
 * Pick a recurring client that fits the contract category, while respecting
 * the 30-day "Méfiant" freeze (no contract proposals from clients whose
 * loyalty just dropped under 20). Falls back to any non-frozen client if
 * no thematic match is found.
 *
 * @param {Object} contract     — contract template (uses `category`)
 * @param {Object} clientLoyalty — state.clientLoyalty (may be empty pre-bootstrap)
 * @param {number} totalDays    — for cooldown checks
 * @returns {string|null} clientId or null when everyone is frozen
 */
export function pickClientForContract(contract, clientLoyalty = {}, totalDays = 0) {
  const category = contract?.category;
  const free = RECURRENT_CLIENTS.filter(c => !isClientOnCooldown(clientLoyalty, c.id, totalDays));
  if (free.length === 0) return null;
  // Theme-match pass — prefer a client whose themes include the contract category.
  const themeMatches = free.filter(c => Array.isArray(c.themes) && c.themes.includes(category));
  const pool = themeMatches.length > 0 ? themeMatches : free;
  return pool[Math.floor(Math.random() * pool.length)].id;
}

// ── 01/06/2026 — RSE premium contracts gate (Claire Maréchal perk) ──
// Tier ordering: none < bronze < silver < gold. A contract requiring
// `silver` is hidden from players whose latest EcoVadis tier ranks below.
const RSE_TIER_RANK = { none: 0, bronze: 1, silver: 2, gold: 3 };

/**
 * Resolve the player's current EcoVadis tier rank from state history.
 * Defaults to 'none' / 0 when no audit has been archived yet.
 */
function getPlayerRseTierRank(state) {
  const last = (state?.ecoVadisHistory || []).slice(-1)[0] || null;
  const tier = last ? last.tier : 'none';
  return RSE_TIER_RANK[tier] || 0;
}

/**
 * Predicate: does this contract template clear the player's RSE gate?
 * Returns true for any contract WITHOUT a `requiresRseTier` flag.
 */
function meetsRseTier(contract, playerTierRank) {
  if (!contract?.requiresRseTier) return true;
  return playerTierRank >= (RSE_TIER_RANK[contract.requiresRseTier] || 0);
}

/**
 * Materialise a contract template into a concrete instance that lands on the
 * market board. Centralises:
 *   • Recurring client picking + theme matching
 *   • Early-game boost (+50 % daily revenue for `régulier` contracts in
 *     phase 1 when reputation < 50) — applied once.
 *   • Ponctuel revenue rolled inside [revenueMin, revenueMax].
 *   • Loyalty price multiplier from the picked client.
 *
 * Pure-ish: relies on Math.random for the ponctuel revenue roll and the
 * client pick, identical to the previous inlined code.
 *
 * @param {Object} template      — contract data from CONTRACTS
 * @param {Object} opts
 * @param {number} opts.phase
 * @param {number} opts.reputation
 * @param {Object} opts.clientLoyalty
 * @param {number} opts.spawnDay
 */
function buildContractInstance(template, { phase, reputation, clientLoyalty, spawnDay, geo, costState }) {
  const clientId = pickClientForContract(template, clientLoyalty || {}, spawnDay);
  const client = clientId ? RECURRENT_CLIENTS.find(rc => rc.id === clientId) : null;
  const priceMul = getClientPriceMultiplier(clientLoyalty || {}, clientId);
  // Early-game boost (28/05/2026) for `régulier` contracts spawned in
  // phase 1 with low reputation — gives the apprenant a stable runway.
  const isEarlyGameBoostEligible = phase === 1
    && (reputation || 0) < 50
    && template.frequency === 'régulier';
  const earlyBoost = isEarlyGameBoostEligible ? 1.5 : 1.0;
  // 17/06/2026 — Rééquilibrage user : les contrats Tier 4 / Tier 5 valaient
  // trop cher et accéléraient la mise en vitesse de croisière. On applique
  // un coefficient correctif uniforme −30 % sur le revenu (dailyRevenue ET
  // prime ponctuelle) ; cohérent avec la prime de signing puisque la prime
  // dérive directement du revenu. Tier 1-3 inchangés.
  const TIER_45_REBALANCE = (template.tierMin === 4 || template.tierMin === 5) ? 0.70 : 1.0;
  const baseRevenue = template.frequency === 'ponctuel'
    ? Math.floor((template.revenueMin + Math.random() * (template.revenueMax - template.revenueMin)) * TIER_45_REBALANCE)
    : Math.round(template.dailyRevenue * earlyBoost * TIER_45_REBALANCE);

  // 13/06/2026 — Chantier Dépôts Phase 1 : chaque contrat naît dans une
  // ville d'origine. ~30 % hors zone (opportunités rares à +30-50 % de
  // revenus — l'incitation à ouvrir des dépôts dans de nouvelles régions).
  let originCityId = null;
  let rareOpportunity = false;
  let farBoost = 1;
  if (geo && geo.ownedDepots && geo.ownedDepots.length > 0) {
    const picked = pickContractOriginCity(geo.ownedDepots, geo.company, phase, Math.random, {
      isExport: template.category === 'Export UE',
    });
    originCityId = picked.originCityId;
    if (picked.far) {
      rareOpportunity = true;
      farBoost = FAR_CONTRACT_BOOST_MIN + Math.random() * FAR_CONTRACT_BOOST_SPAN;
    }
  }

  return {
    ...template,
    // Persist the boosted dailyRevenue so the in-game revenue calculation
    // (in nextDay) automatically uses the higher number too.
    // 25/07/2026 (retour user « réguliers quasi jamais rentables ») —
    // plancher de rentabilité : jamais sous le coût réel du binôme le
    // moins cher capable + marge minimale (cf. computeRegularFloorDaily).
    dailyRevenue: template.frequency === 'régulier'
      ? Math.max(
        Math.round(template.dailyRevenue * earlyBoost * farBoost * TIER_45_REBALANCE),
        costState ? computeRegularFloorDaily(costState, template) : 0
      )
      : template.dailyRevenue,
    instanceId: generateId(),
    revenue: template.frequency === 'régulier'
      ? Math.max(
        Math.round(baseRevenue * priceMul * farBoost),
        costState ? computeRegularFloorDaily(costState, template) : 0
      )
      : Math.round(baseRevenue * priceMul * farBoost),
    spawnDay,
    clientId,
    clientName: client ? client.name : template.client || 'Client',
    originCityId,
    rareOpportunity,
  };
}

/**
 * 28/07/2026 — Action client « Solliciter une mission » : construit un
 * contrat PONCTUEL dédié offert par le client sollicité. Thème préféré du
 * client quand possible, compatible avec la flotte possédée (tierMin).
 * Retourne null si aucun template ne convient.
 */
const THEME_CATEGORY_PATTERNS = {
  urbain: /messagerie|colis/i,
  'régional': /vrac|palettes|messagerie/i,
  national: /vrac|palettes/i,
  frigorifique: /frigo/i,
  lourd: /btp|lourd|exceptionnel/i,
  international: /export/i,
};

export function buildSollicitedContract(state, client) {
  const maxTier = Math.max(1, ...(state.vehicles || []).map(v => v.tier || 1));
  const ponctuels = CONTRACTS.filter(t => t.frequency === 'ponctuel' && t.tierMin <= maxTier);
  if (ponctuels.length === 0) return null;
  const themed = ponctuels.filter(t =>
    (client.themes || []).some(th => (THEME_CATEGORY_PATTERNS[th] || /$^/).test(t.category || '')),
  );
  const pool = themed.length > 0 ? themed : ponctuels;
  const template = pool[Math.floor(Math.random() * pool.length)];
  const instance = buildContractInstance(template, {
    phase: state.phase || 1,
    reputation: state.reputation || 0,
    clientLoyalty: state.clientLoyalty || {},
    spawnDay: state.totalDays || 0,
    geo: { ownedDepots: state.ownedDepots || [], company: state.company },
    costState: state,
  });
  return {
    ...instance,
    name: `Mission dédiée — ${template.name}`,
    clientId: client.id,
    clientName: client.name,
    sollicitedByPlayer: true,
    // Prime « mission dédiée » : +10 % sur la prime ponctuelle.
    revenue: Math.round((instance.revenue || 0) * 1.1),
    boardTtlDays: 10,
  };
}

/**
 * 03/06/2026 — Cible de contrats à afficher au tableau.
 *
 * L'utilisateur a remonté qu'au démarrage il y avait trop de contrats
 * (« la prospection est faite par le gérant »). Le nombre doit varier
 * entre 3 et 7 au lancement (gérant seul), puis évoluer avec :
 *   - les fonctions support Marketing/Commerce recrutées
 *   - la réputation (au-dessus de 50)
 *   - la notoriété
 *
 * Détail du calcul :
 *   base       = aléatoire 3..7, stable par semaine (totalDays / 7).
 *   + supports = Rémi Forni +2, Léa +2, Marc +3, Sylvie +3, Camille +1
 *   + repExcess = max(0, (reputation - 50) / 10)        // 0..5
 *   + notoBonus = floor(notoriety / 25)                  // 0..4
 *   cap        = 18 contrats visibles maximum
 *
 * Pure : ne lit que `state` (déterministe pour un (totalDays, supports, rep, noto) donné).
 */
function _seededRand(seed) {
  // Mulberry32 — déterministe et léger
  let t = (seed * 1664525 + 1013904223) >>> 0;
  t = (t + 0x6D2B79F5) >>> 0;
  let r = Math.imul(t ^ (t >>> 15), 1 | t);
  r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
  return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
}

export function computeContractTarget(state) {
  const totalDays = state?.totalDays || 0;
  const reputation = state?.reputation || 0;
  const notoriety = state?.notoriety || 0;
  const hiredSupport = state?.hiredSupport || {};

  const weekSeed = Math.floor(totalDays / 7);
  // Base aléatoire 3..7 inclus, stable par semaine.
  const base = 3 + Math.floor(_seededRand(weekSeed + 1) * 5);

  let supportBoost = 0;
  if (hiredSupport['role-christophe-faure']) supportBoost += 2;
  if (hiredSupport['role-sherazade-fetitou']) supportBoost += 1;
  if (hiredSupport['role-lea-bertrand']) supportBoost += 2;
  if (hiredSupport['role-marc-dupuis']) supportBoost += 3;
  if (hiredSupport['role-sylvie-marchand']) supportBoost += 3;

  const repExcess = Math.max(0, Math.floor((reputation - 50) / 10));
  const notoBonus = Math.floor(notoriety / 25);

  const target = base + supportBoost + repExcess + notoBonus;
  return Math.max(1, Math.min(18, target));
}

/**
 * Generate available contracts for the market board.
 *
 * Scales with reputation AND fleet size to avoid the late-game frustration
 * where a 10-truck/10-driver player only sees 5-7 contracts on the board.
 *
 * Coverage rule: at most 1 contract per category is returned per roll, until
 * we've populated `count` slots, then we wrap to allow duplicates from the
 * highest-margin categories. This guarantees variety even when phase 2+
 * unlocks 6-8 categories.
 *
 * @param {number} phase        - current phase (1-4)
 * @param {number} maxTier      - highest tier owned (1-4)
 * @param {number} reputation   - 0-100
 * @param {number} fleetSize    - total owned + leased vehicles (default 0)
 * @param {object} options      - { targetCount } to override the auto-sized count
 */
export const generateAvailableContracts = (phase, maxTier, reputation, fleetSize = 0, options = {}) => {
  // 08/02/2026 — Refonte phases (Q2). Plafond Tier en plus du maxTier
  // (= meilleur véhicule possédé) : la phase gate aussi le board.
  //   P1 → Tier 1-2  •  P2 → +Tier 3  •  P3+ → +Tier 4-5
  // Évite qu'un joueur P1 voit déjà des contrats Tier 5 même en ayant
  // hérité d'un camion lourd.
  const phaseTierCap = getMaxContractTierForPhase(phase || 1);
  const effectiveMaxTier = Math.min(maxTier, phaseTierCap);
  const eligible = CONTRACTS.filter(c => c.tierMin <= effectiveMaxTier);
  // 03/06/2026 — Si un `options.state` est fourni, on utilise le nouveau
  // calcul de cible (computeContractTarget). Sinon, on retombe sur la
  // formule historique (base 4 + rep/25 + fleet × 1.2) — utilisée par
  // quelques tests unitaires qui passent params plats.
  const autoCount = options.state
    ? computeContractTarget(options.state)
    : (4 + Math.floor(reputation / 25) + Math.ceil(fleetSize * 1.2));
  const count = Math.min(eligible.length, options.targetCount || autoCount, 20);
  if (count <= 0) return [];

  // Variety pass: group by category, shuffle each bucket, then round-robin.
  const byCategory = {};
  for (const c of eligible) {
    (byCategory[c.category] = byCategory[c.category] || []).push(c);
  }
  Object.values(byCategory).forEach(arr => arr.sort(() => Math.random() - 0.5));
  const categoryOrder = Object.keys(byCategory).sort(() => Math.random() - 0.5);

  const picks = [];
  let safety = count * 4;
  let catIdx = 0;
  while (picks.length < count && safety-- > 0) {
    const cat = categoryOrder[catIdx % categoryOrder.length];
    const bucket = byCategory[cat];
    if (bucket && bucket.length > 0) {
      const c = bucket.shift();
      // Avoid duplicates of the exact same contract template.
      if (!picks.find(p => p.id === c.id)) picks.push(c);
    }
    catIdx++;
  }

  return picks.map(c => buildContractInstance(c, {
    phase,
    reputation,
    clientLoyalty: options.clientLoyalty,
    spawnDay: options.spawnDay || 0,
    costState: options.state || null,
    geo: (options.state && options.state.ownedDepots)
      ? { ownedDepots: options.state.ownedDepots, company: options.state.company }
      : options.geo,
  }));
};

/**
 * Top up the contract board incrementally — used by the daily tick instead of
 * a full weekly wipe-and-regenerate. Removes contracts older than 14 days,
 * then adds enough fresh ones to reach the auto-sized target. Returns the
 * new array.
 */
// 16/06/2026 — TTL exporté + helper de validité : les contrats quittent le
// marché 14 jours après publication. Désormais visible côté joueur via le
// badge « Valide J-x » (vue tableau + panneau liège).
export const CONTRACT_BOARD_TTL_DAYS = 14;

/**
 * Remaining validity days for a board contract (null when the contract has
 * no spawnDay — defensive for legacy saves).
 *
 * 06/02/2026 — P1 retour user prod : certains contrats (ex.
 * "Messagerie médicale urgente" `msg-06`) doivent expirer plus vite que la
 * fenêtre standard de 14 j car ils sont URGENTS par essence. On respecte
 * désormais un éventuel champ `boardTtlDays` posé sur le contrat lui-même
 * (cf. data/contracts.js).
 */
export const getContractValidityDaysLeft = (contract, totalDays) => {
  if (!contract || !Number.isFinite(contract.spawnDay)) return null;
  const ttl = Number.isFinite(contract.boardTtlDays) && contract.boardTtlDays > 0
    ? contract.boardTtlDays
    : CONTRACT_BOARD_TTL_DAYS;
  return Math.max(0, ttl - ((totalDays || 0) - contract.spawnDay));
};

export const topUpAvailableContracts = (state, currentContracts) => {
  const phase = state.phase || 1;
  const rawMaxTier = state.vehicles.length > 0 ? Math.max(...state.vehicles.map(v => v.tier), 1) : 1;
  // 08/02/2026 — Refonte phases (Q2) : phase tier cap.
  const maxTier = Math.min(rawMaxTier, getMaxContractTierForPhase(phase));
  const totalDays = state.totalDays || 0;

  // Drop stale contracts (> 14 days on the board, or > boardTtlDays for urgent
  // contracts like msg-06 that carry a custom ttl).
  const fresh = (currentContracts || []).filter(c => {
    const ttl = Number.isFinite(c.boardTtlDays) && c.boardTtlDays > 0
      ? c.boardTtlDays
      : CONTRACT_BOARD_TTL_DAYS;
    return (totalDays - (c.spawnDay || 0)) <= ttl;
  });
  // 24/07/2026 — Contrats locaux export (demande user) : le cabotage d'un
  // pays apparaît sur le marché dès qu'un chauffeur LOCAL de ce pays est
  // dans l'effectif. 25/07/2026 : injectés EN PLUS de la cible standard,
  // MÊME quand le board est déjà plein (avant, l'early-return les zappait).
  const localCountries = new Set((state.drivers || []).map(d => d.localCountry).filter(Boolean));
  let localFillers = [];
  if (localCountries.size > 0) {
    const boardIds = new Set(fresh.map(c => c.id));
    const activeIds = new Set((state.activeContracts || []).map(c => c.id));
    localFillers = LOCAL_CONTRACTS
      .filter(c => localCountries.has(c.requiresLocalCountry)
        && c.tierMin <= maxTier
        && !boardIds.has(c.id)
        && !activeIds.has(c.id))
      .map(c => buildContractInstance(c, {
        phase,
        reputation: state.reputation || 0,
        clientLoyalty: state.clientLoyalty,
        spawnDay: totalDays,
        geo: null, // opération montée sur place — pas de ville d'origine FR
      }));
  }

  // 03/06/2026 — cible basée sur supports + reputation > 50 + notoriété.
  const eligibleCount = CONTRACTS.filter(c => c.tierMin <= maxTier).length;
  const target = Math.min(eligibleCount, computeContractTarget(state));
  if (fresh.length >= target) {
    return localFillers.length > 0 ? [...fresh, ...localFillers] : fresh;
  }

  // RSE gate (01/06/2026, Claire Maréchal): contracts requiring a tier
  // only spawn once the player has reached that tier.
  const playerTierRank = getPlayerRseTierRank(state);

  // Add new contracts until target is reached, biased toward categories not
  // already represented on the board.
  const onBoard = new Set(fresh.map(c => c.id));
  const candidates = CONTRACTS.filter(c =>
    c.tierMin <= maxTier && !onBoard.has(c.id) && meetsRseTier(c, playerTierRank)
  );
  candidates.sort(() => Math.random() - 0.5);
  const needed = target - fresh.length;
  const fillers = candidates.slice(0, needed).map(c => buildContractInstance(c, {
    phase,
    reputation: state.reputation || 0,
    clientLoyalty: state.clientLoyalty,
    spawnDay: totalDays,
    costState: state,
    geo: { ownedDepots: state.ownedDepots, company: state.company },
  }));

  return [...fresh, ...fillers, ...localFillers];
};

export const generateSpecialContract = (state) => {
  if (state.totalDays < 5 || Math.random() > 0.10) return null;
  const phase = state.phase || 1;
  const rawMaxTier = Math.max(...state.vehicles.map(v => v.tier), 1);
  const maxTier = Math.min(rawMaxTier, getMaxContractTierForPhase(phase));
  // 25/07/2026 (retour user « contrats spéciaux pas toujours acceptables
  // alors qu'on possède les camions et les spécialités ») — un contrat
  // spécial n'apparaît que si le joueur possède AU MOINS un binôme
  // camion + chauffeur QUALIFIÉ (peu importe sa disponibilité du moment) :
  // fini les offres structurellement impossibles à accepter.
  const eligible = SPECIAL_CONTRACTS.filter(c => {
    if (c.tierMin > maxTier) return false;
    const okV = (state.vehicles || []).filter(v => v.status !== 'sold' && vehicleSatisfiesContract(v, c).ok);
    if (okV.length === 0) return false;
    const okD = (state.drivers || []).filter(d => driverSatisfiesContract(d, c).ok);
    if (okD.length === 0) return false;
    return okV.some(v => okD.some(d => driverCanDriveVehicle(d, v).ok));
  });
  if (eligible.length === 0) return null;
  const pick = eligible[Math.floor(Math.random() * eligible.length)];
  // 10/07/2026 — fix user prod « infos client absentes en négociation » :
  // les contrats spéciaux naissaient SANS client → l'écran d'argumentation
  // n'avait ni profil ni tells à afficher. On leur attribue un client
  // récurrent comme les contrats normaux.
  const clientId = pickClientForContract(pick, state.clientLoyalty || {}, state.totalDays || 0);
  const client = clientId ? RECURRENT_CLIENTS.find(rc => rc.id === clientId) : null;
  return {
    ...pick,
    instanceId: generateId(),
    revenue: pick.dailyRevenue * pick.duration,
    isSpecial: true,
    clientId,
    clientName: client ? client.name : pick.client || 'Client',
    // 16/06/2026 — les contrats spéciaux portent désormais un spawnDay :
    // même TTL 14 j que le reste du tableau + badge « Valide J-x » correct.
    spawnDay: state.totalDays || 0,
  };
};

// 10/07/2026 — PHASE 4 macro-événements : organiques uniquement à partir
// de l'année 2. Le superviseur pédagogique peut toujours en pousser un via
// les scénarios Phase 3 (scenarioPushSlice), sans restriction de date.
export const MACRO_EVENTS_MIN_DAY = 365;
export const ZFE_METROPOLES = ['paris', 'lyon', 'marseille'];

export const isMacroEventEligible = (e, state) => {
  if ((state.totalDays || 0) < MACRO_EVENTS_MIN_DAY) return false;
  // Un seul choc structurel actif à la fois.
  if (state.zfeBan || state.fuelShock || state.companyStrike) return false;
  if (e.id === 'evt-macro-zfe') {
    const hq = (state.ownedDepots || []).find(d => d.isHeadquarters);
    if (!ZFE_METROPOLES.includes(hq?.cityId)) return false;
  }
  if (e.winterOnly && ![11, 12, 1, 2].includes(state.month)) return false;
  if (e.oneShot && (state.eventLog || []).some(l => l.id === e.id)) return false;
  if (e.id === 'evt-macro-convention' && !(state.drivers || []).length) return false;
  return true;
};

export const generateRandomEvent = (state, eventProbMultiplier = 1.0) => {
  // Early-game guaranteed event: the retirement of a colleague triggers on the
  // player's very first "Next day" tick. The event is generated BEFORE totalDays
  // is incremented, so we check state.totalDays === 0 (game start) — after this
  // tick the day will become 1 (J2 in the UI).
  if (state.totalDays === 0 && !(state.eventLog || []).some(e => e.id === 'evt-occasion-01')) {
    const earlyEvent = EVENTS.find(e => e.id === 'evt-occasion-01');
    if (earlyEvent) return { ...earlyEvent, instanceId: generateId(), resolvedAt: null };
  }

  // Resource gates — categorically prevent absurd events for the current state:
  //   - 0 vehicles  → no Panne / Accident (Energie OK = fuel station news,
  //                   Adminis = only litige client / control survives if it
  //                   does not require a truck)
  //   - 0 drivers   → no Personnel events
  //   - 0 vehicles AND 0 drivers → only generic Marché / Crise / Météo events
  const hasVehicles = (state.vehicles || []).length > 0;
  const hasDrivers  = (state.drivers  || []).length > 0;
  // 08/02/2026 — Q2 user prod : empêcher les aléas liés à la ROUTE ou à un
  // CONTRAT actif quand le joueur n'a aucun contrat en cours. Sinon on a
  // l'absurde « panne moteur en livraison » alors que tous les camions
  // sont au parking. Catégories concernées : Panne mécanique, Accident,
  // Météo (impacte les trajets), Social/Grève (blocage routier), litige
  // client. Si un seul contrat actif suffit, on ne bloque pas.
  const hasActiveContracts = (state.activeContracts || []).some(c => c.vehicleId);
  const ROUTE_OR_CONTRACT_CATEGORIES = new Set([
    'Panne mécanique',
    'Accident',
    'Météo',
    'Social / Grève',
  ]);
  const CONTRACT_BOUND_EVENT_IDS = new Set([
    'evt-admin-02', // Litige client
    'evt-accident-03', // Perte de chargement
  ]);

  // Subset of admin events that don't require a vehicle to be coherent.
  const ADMIN_NO_VEHICLE_OK = new Set([
    'evt-admin-02', // Litige client
  ]);

  const eligible = EVENTS.filter(e => {
    if (e.earlyGame) return false;
    // SCD Lot 3 — événements retail : uniquement si un magasin est actif.
    if (e.retail && !state.retail) return false;
    // 17/06/2026 — Étapes d'arc narratif (ex. evt-rh-fuel-theft-investigate)
    // ne se déclenchent JAMAIS spontanément : `probaMonth: 0`. Elles sont
    // créées par advanceEventArc() en réponse à un choix d'étape précédente.
    if (!e.probaMonth || e.probaMonth <= 0) return false;
    // --- Coherence gates ---
    if (!hasVehicles && (e.category === 'Panne mécanique' || e.category === 'Accident')) return false;
    if (!hasVehicles && e.category === 'Administratif' && !ADMIN_NO_VEHICLE_OK.has(e.id)) return false;
    if (!hasDrivers  && e.category === 'Personnel') return false;
    // 18/06/2026 — fix user prod « arrêt maladie fantôme » : les aléas
    // RH interne exigent au moins un chauffeur salarié. evt-rh-02 exige
    // en plus un chauffeur ciblable (actif ou dispo) ; evt-rh-04 exige
    // au moins 2 chauffeurs (conflit d'équipe).
    if (!hasDrivers && e.category === 'RH interne') return false;
    if (e.id === 'evt-rh-02' && !(state.drivers || []).some(d => d.status === 'active' || d.status === 'idle')) return false;
    if (e.id === 'evt-rh-04' && (state.drivers || []).length < 2) return false;
    // 08/02/2026 — Gate route/contract events on at least one active contract.
    if (!hasActiveContracts && ROUTE_OR_CONTRACT_CATEGORIES.has(e.category)) return false;
    if (!hasActiveContracts && CONTRACT_BOUND_EVENT_IDS.has(e.id)) return false;
    // 08/02/2026 — Q2 Lot A user prod : evt-panne-04 (Panne groupe froid)
    // ne se déclenche QUE si (a) le joueur a au moins un contrat frigo
    // actif ET (b) une zone frigo investie au dépôt (inv-energie-02 ou
    // inv-energie-04 ?). Sinon c'est incohérent.
    if (e.requiresFrigoContext) {
      const hasFrigoContract = (state.activeContracts || []).some(c =>
        (c.specialty || '').toLowerCase().includes('frigo') ||
        (c.specialty || '').toLowerCase().includes('frais')
      );
      const hasFrigoZone = !!(state.activeInvestments || {})['inv-depot-05']; // Zone frigorifique au dépôt
      if (!hasFrigoContract || !hasFrigoZone) return false;
    }
    // --- Catastrophes (13/06/2026) : rares, violentes, late-game only ---
    if (e.category === 'Catastrophe') {
      if ((state.totalDays || 0) < 120) return false;
      if ((state.vehicles || []).length < 3) return false;
      if (e.id === 'evt-cata-02' && !(state.vehicles || []).some(v => !v.isLeased && v.status !== 'sold' && v.status !== 'selling')) return false;
      if (e.id === 'evt-cata-03' && !(state.activeContracts || []).some(c => c.vehicleId)) return false;
      // 17/06/2026 — Nouveaux aléas catastrophe : rupture moteur exige un
      // véhicule possédé > 1000 j cumulés, cyber exige TMS, vandalisme
      // exige au moins 1 dépôt non-QG.
      if (e.id === 'evt-cata-engine-rupture' && !(state.vehicles || []).some(v => !v.isLeased && (v.totalDistance || v.kmTotal || 0) > 0)) return false;
      if (e.id === 'evt-cata-cyber' && !((state.activeInvestments || {})['inv-tech-01'])) return false;
      if (e.id === 'evt-cata-vandalism' && !(state.ownedDepots || []).some(d => !d.isHeadquarters)) return false;
    }
    // 17/06/2026 — Douanes : impact proportionnel ne se déclenche qu'à
    // partir d'une flotte ≥ 5 véhicules (sinon non sensé). Cf.
    // scaleByFleetSize ci-dessous (multiplicateur appliqué après).
    if (e.id === 'evt-customs-01' && (state.vehicles || []).length < 5) return false;
    // 10/07/2026 — PHASE 4 macro-événements : année 2 minimum + exclusivité
    // des chocs structurels + gates de cohérence (ZFE métropole, crue hiver,
    // convention collective one-shot). Cf. isMacroEventEligible.
    if (e.macro && !isMacroEventEligible(e, state)) return false;
    // 07/02/2026 — P0 retour user : "Liquidation d'une société de transport"
    // proposait un tier 4 à -50 % dès le mois 4 → cassait la courbe de
    // progression économique. Gate : mois 8 minimum (240 j) + cap dynamique
    // sur la tier (vehicleOptions filtrées plus bas après sélection).
    if (e.id === 'evt-occasion-04' && (state.totalDays || 0) < 240) return false;
    // ---
    const dailyProba = (e.probaMonth / 30) * eventProbMultiplier;
    return Math.random() < dailyProba;
  });
  if (eligible.length === 0) return null;
  // 08/02/2026 — Q2 Lot A user prod : pondération des probas pour les
  // events Panne mécanique selon l'état de la flotte (wear) + présence
  // Mehdi/Bruno + fatigue chauffeur. Voir computeBreakdownRiskMultiplier.
  // 08/02/2026 — Q2 Lot D user prod : on étend la pondération aux
  // catégories Social/Grève (satisfaction + tempéraments) et
  // Administratif (support staff + audit IT). Cf. eventRiskMultipliers.
  // On pondère via `probaMonth × multiplier` puis on fait un weighted pick.
  const weights = eligible.map(e => {
    let w = e.probaMonth || 0.1;
    if (e.category === 'Panne mécanique' || e.category === 'Accident') {
      // breakdown risk model lazy-import to avoid circular dep
      // eslint-disable-next-line global-require
      const { computeBreakdownRiskMultiplier } = require('./helpers/breakdownRisk');
      w *= computeBreakdownRiskMultiplier(state, null);
    }
    if (e.category === 'Social / Grève') {
      // eslint-disable-next-line global-require
      const { computeSocialRiskMultiplier } = require('./helpers/eventRiskMultipliers');
      w *= computeSocialRiskMultiplier(state);
    }
    if (e.category === 'Administratif') {
      // eslint-disable-next-line global-require
      const { computeAdminRiskMultiplier } = require('./helpers/eventRiskMultipliers');
      w *= computeAdminRiskMultiplier(state);
    }
    // Arc vol de carburant — vidéosurveillance/IT divisent la proba.
    if (e.id === 'evt-rh-fuel-theft-detect') {
      // eslint-disable-next-line global-require
      const { computeFuelTheftRiskMultiplier } = require('./helpers/eventRiskMultipliers');
      w *= computeFuelTheftRiskMultiplier(state);
    }
    return w;
  });
  const totalWeight = weights.reduce((s, w) => s + w, 0);
  if (totalWeight <= 0) return null;
  let r = Math.random() * totalWeight;
  let pickIndex = 0;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r <= 0) { pickIndex = i; break; }
  }
  const event = eligible[pickIndex];

  // 02/06/2026 — Difficulty escalation : event costs scale with years
  // played. Year 1 = baseline, every additional year adds +10% to costs
  // (capped at +60% in year 7+). Reputation impact is left unchanged
  // — only the cash sting grows. Player feedback: "Augmenter la
  // difficulté des aléas au fur et à mesure du jeu".
  const yearsPlayed = Math.max(0, Math.floor((state.totalDays || 0) / 360));
  const costMult = 1 + Math.min(0.60, yearsPlayed * 0.10);

  // 17/06/2026 — Aléa Douanes : amende proportionnelle à la taille de la
  // flotte. Multiplicateur 1× (5 véhicules) → 3× (30+ véhicules), lisse.
  let scaleMult = 1;
  if (event.scaleByFleetSize) {
    const fleet = (state.vehicles || []).filter(v => v.status !== 'sold').length;
    scaleMult = Math.min(3, Math.max(1, fleet / 10));
  }
  const totalMult = costMult * scaleMult;

  const scaled = { ...event, instanceId: generateId(), resolvedAt: null };
  if (typeof event.costMin === 'number') scaled.costMin = Math.round(event.costMin * totalMult);
  if (typeof event.costMax === 'number') scaled.costMax = Math.round(event.costMax * totalMult);
  if (Array.isArray(event.choices)) {
    scaled.choices = event.choices.map(c => ({
      ...c,
      cost: typeof c.cost === 'number' ? Math.round(c.cost * totalMult) : c.cost,
      costMin: typeof c.costMin === 'number' ? Math.round(c.costMin * totalMult) : c.costMin,
      costMax: typeof c.costMax === 'number' ? Math.round(c.costMax * totalMult) : c.costMax,
    }));
  }
  scaled.difficultyMultiplier = totalMult;

  // 07/02/2026 — P0 retour user : cap dynamique des véhicules proposés dans
  // les aléas d'occasion. Le tier MAX autorisé = max(tier en flotte) + 1.
  // Sans ce cap, un joueur tier 2 pouvait acheter un tier 4 (skip de la
  // progression métier). Filtre aussi les `choices` rattachées à ces
  // vehicleOptions. Si aucune option ne passe le cap, on retombe sur l'aléa
  // sans options achetables (toutes les `specialAction.type === 'buyUsedVehicle'`
  // sont supprimées). L'événement reste affiché pour la narration.
  if (event.isUsedVehicleEvent && Array.isArray(scaled.choices)) {
    const fleetMaxTier = (state.vehicles || []).length > 0
      ? Math.max(...state.vehicles.map(v => v.tier || 1), 1)
      : 1;
    const maxAllowedTier = fleetMaxTier + 1;
    const vehicleTierById = Object.fromEntries(
      (VEHICLES || []).map(v => [v.id, v.tier || 1]),
    );
    const isOverTierChoice = (c) => {
      const dataId = c?.specialAction?.vehicleDataId;
      if (!dataId) return false;
      const t = vehicleTierById[dataId] || 1;
      return t > maxAllowedTier;
    };
    const filteredChoices = scaled.choices.filter(c => !isOverTierChoice(c));
    // Toujours garder au moins l'option "Passer son tour / Décliner".
    if (filteredChoices.length > 0) {
      scaled.choices = filteredChoices;
    }
    if (Array.isArray(scaled.vehicleOptions)) {
      scaled.vehicleOptions = scaled.vehicleOptions.filter(o => {
        const t = vehicleTierById[o.vehicleDataId] || 1;
        return t <= maxAllowedTier;
      });
    }
  }

  // 18/06/2026 — fix user prod « arrêt maladie fantôme » : l'aléa cible
  // désormais un chauffeur PRÉCIS dès le tirage. Le popup affiche son nom
  // et resolveEvent applique l'arrêt à CE chauffeur (quel que soit le choix).
  if (event.id === 'evt-rh-02') {
    const drivers = state.drivers || [];
    const target = drivers.find(d => d.status === 'active' && d.assignedVehicleId)
      || drivers.find(d => d.status === 'active')
      || drivers.find(d => d.status === 'idle');
    if (!target) return null;
    scaled.sickDriverId = target.id;
    scaled.sickDriverName = target.name;
    scaled.name = `Arrêt maladie — ${target.name}`;
  }

  // 10/07/2026 — fix user prod « popup démission sans nom » : même principe
  // que l'arrêt maladie, l'aléa cible un chauffeur PRÉCIS dès le tirage et
  // la description chiffre l'impact réel (salaire, mission en cours).
  if (event.id === 'evt-rh-01') {
    const drivers = state.drivers || [];
    // 23/07/2026 — fix user prod « chauffeur en formation qui disparaît » :
    // plus JAMAIS de fallback drivers[0] (pouvait cibler un chauffeur en
    // formation/arrêt/congés). Seuls les chauffeurs actifs ou dispo au dépôt
    // peuvent démissionner via cet aléa ; sinon l'événement ne se tire pas.
    const target = drivers.find(d => d.status === 'active' && d.assignedVehicleId)
      || drivers.find(d => d.status === 'active')
      || drivers.find(d => d.status === 'idle' && !d.onHoliday);
    if (!target) return null;
    scaled.resigningDriverId = target.id;
    scaled.resigningDriverName = target.name;
    scaled.name = `Démission — ${target.name}`;
    const salary = target.salaryGross || 0;
    scaled.description = `${target.name} (${salary.toLocaleString('fr-FR')} €/mois brut) a reçu une offre d'un concurrent et menace de partir. ` +
      (target.assignedVehicleId
        ? `Il/elle est actuellement EN MISSION : son départ libérerait le véhicule et laisserait le contrat en cours sans chauffeur.`
        : `Il/elle est actuellement disponible au dépôt.`);
  }

  return scaled;
};
